/** Callers switch on `code`, never on `message`: the message is for the doctor
 *  to read, the code is for the client to reason about. */

export interface ApiErrorDetail {
  field?: string
  issue?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: ApiErrorDetail[]
  readonly correlationId: string | null

  constructor(
    status: number,
    code: string,
    message: string,
    details: ApiErrorDetail[] = [],
    correlationId: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.correlationId = correlationId
  }

  /** The browser itself says there is no connection. */
  static offline(): ApiError {
    return new ApiError(
      0,
      'offline',
      'You are offline. Anything you typed is kept on this device.',
    )
  }

  /** `fetch` throws the same TypeError for a dropped connection, a CORS refusal
   *  and a programming mistake, so this claims only what is known. */
  static network(): ApiError {
    return new ApiError(
      0,
      'network_error',
      'Could not reach the clinic server. Anything you typed is kept on this device.',
    )
  }

  /** Either of the two above: nothing reached the server. */
  get isNetwork(): boolean {
    return this.status === 0
  }

  get isOffline(): boolean {
    return this.code === 'offline'
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }


  get isConflict(): boolean {
    return this.status === 409
  }

  get isStaleWrite(): boolean {
    return this.code === 'stale_write'
  }

  // No `fieldError` accessor here on purpose: `issue` is a code, not a
  // sentence, and a getter that looks display-ready is how "string_too_short"
  // reached a patient form. Use `fieldMessages` from ./messages instead.
}
